import React, { useState, useEffect } from 'react';
import { Box, Tab, Tabs, Paper, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import {
    Person as PersonIcon, Work as WorkIcon, AttachMoney as AttachMoneyIcon, VerifiedUser as VerifiedUserIcon,
    NoteAdd as NoteAddIcon, ExitToApp as ExitToAppIcon, Policy as PolicyIcon, ArrowBack
} from '@mui/icons-material';
import axios from 'axios';
import { axiosInstance, localAxiosGet } from '../../../../Axios/axios';
import TokenExpirePopup from '../../../../components/TokenExpirePopup';
import { useNavigate } from 'react-router-dom';

// --- Placeholder Component Imports ---
import PersonalContactDetails from './PersonalContactDetails';
import EmploymentDetails from './EmploymentDetails';
import PayrollDetails from './PayrollDetails';
import PolicyAssignment from './PolicyAssignment';
import BankingStatutory from './BankingStatutory';
import DocumentsOther from './DocumentsOther';
import ExitRehireStatus from './ExitRehireStatus';

// Import Modal and mockData from the Modal file
import ViewAllDetailsModal, { mockAllEmployeeData } from './EmployeeDetailsAllDisplayModal';

// Custom Styled Components
const StyledTabs = styled(Tabs)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    marginBottom: theme.spacing(3),
    paddingY: '6px',
    '& .MuiTabs-indicator': { height: 0, },
    '& .MuiTab-root': {
        fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'none',
        minHeight: 48, borderRadius: 8, margin: '0 4px',
        '&.Mui-selected': {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.light + '20',
        },
    },
}));

const ContentPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3), borderRadius: 16,
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
    backgroundColor: theme.palette.background.paper,
    minHeight: 'calc(100vh - 280px)',
}));

const ViewEmployeeDetails = ({ userId, organizationId, onClose,onFormCompletionChange }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [employeeData, setEmployeeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [navigationContext, setNavigationContext] = useState(null);
    const [openTokenExpirePopup, setOpenTokenExpirePopup] = useState(false);
    const navigate = useNavigate()

    const [rehireChecklist, setRehireChecklist] = useState({
        personal: false,
        employee: false,
        compensation: false,
        policies: false,
        banking: false,
        documents: false,
    });

    const [rehireDetails, setRehireDetails] = useState({
        rehireDate: '',
        rehireReason: ''
    });

    const [isRehireMode, setIsRehireMode] = useState(false);

    // COMMENTED: Form completion state tracking
    /*
    const [completionState, setCompletionState] = useState({
        personal: 0,
        employee: 0,
        compensation: 0,
        banking: 0,
        documents: 0,
        policies: 0
    });
    */

    // ✅ REMOVE THIS useEffect - it's resetting isRehireMode
    // useEffect(() => {
    //     if (employeeData) {
    //         const hasRehireData =
    //             employeeData.rehireDetails?.rehireDate ||
    //             Object.values(employeeData.rehireChecklist || {}).some(v => v === true);
    //         setIsRehireMode(hasRehireData);
    //         if (employeeData.rehireChecklist) {
    //             setRehireChecklist(employeeData.rehireChecklist);
    //         }
    //         if (employeeData.rehireDetails) {
    //             setRehireDetails(employeeData.rehireDetails);
    //         }
    //     }
    // }, [employeeData]);

    const handleUpdateRehireDetails = (details) => {
        console.log('🔍 Parent received rehire details update:', details);
        setRehireDetails(details);
    };

    const handleUpdateRehireChecklist = (updatedChecklist) => {
        console.log('🔍 Parent received checklist update:', updatedChecklist);
        setRehireChecklist(updatedChecklist);
    };

    const handleStartRehire = (isStarting) => {
        console.log('🔍 Rehire mode changed to:', isStarting);
        setIsRehireMode(isStarting);

        // Reset rehire data if stopping rehire process
        if (!isStarting) {
            setRehireChecklist({
                personal: false,
                employee: false,
                compensation: false,
                policies: false,
                banking: false,
                documents: false,
            });
            setRehireDetails({
                rehireDate: '',
                rehireReason: ''
            });
        }
        fetchEmployeeData();
    };

    const handleRehireComplete = () => {
        console.log('🔍 Rehire process completed');
        setIsRehireMode(false);
        
        // Reset all rehire data
        setRehireChecklist({
            personal: false,
            employee: false,
            compensation: false,
            policies: false,
            banking: false,
            documents: false,
        });
        setRehireDetails({
            rehireDate: '',
            rehireReason: ''
        });
        
        // Refresh employee data to get updated status
        fetchEmployeeData();
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        // ✅ Don't clear navigation context - keep it for rehire flow
        // if (navigationContext && !navigationContext.returnToRehire) {
        //     setNavigationContext(null);
        // }
    };

    // COMMENTED: Form completion percentage handler
    /*
    const handleTabFormCompletionChange = (tabKey, completionPercent) => {
        setCompletionState(prev => {
            const updated = { ...prev, [tabKey]: completionPercent };

            // calculate average
            const totalTabs = Object.keys(updated).length;
            const average = Object.values(updated).reduce((a, b) => a + b, 0) / totalTabs;

            // Send to parent (if you need to track total form progress)
            if (typeof onFormCompletionChange === "function") {
                onFormCompletionChange(Math.round(average));
            }

            return updated;
        });
    };
    */

    const handleModalOpen = () => setIsModalOpen(true);
    const handleModalClose = () => setIsModalOpen(false);

    const handleNavigateToTab = (tabIndex, context) => {
        setNavigationContext(context);
        setActiveTab(tabIndex);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSaveAndReturnToRehire = (stepKey) => {
        const updatedChecklist = {
            ...rehireChecklist,
            [stepKey]: true
        };
        console.log('🔍 Updating checklist:', stepKey, updatedChecklist);

        setRehireChecklist(updatedChecklist);
        
        // ✅ Keep isRehireMode true when returning
        // Don't touch isRehireMode here - it should stay true

        toast.success(`✅ Step completed: ${stepKey.charAt(0).toUpperCase() + stepKey.slice(1)}`);
        setNavigationContext(null);
        setActiveTab(6);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const fetchEmployeeData = async () => {
        if (!userId) {
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');
            const response = await axiosInstance(localAxiosGet(`/organization/getUserDetailsByUserId?userId=${userId}`, token));

            if (response.data && response.data.status === "SUCCESS" && response.data.message && response.data.message.user) {
                const userData = response.data.message.user;
                setEmployeeData(userData);
                
                // ✅ Only load rehire data on initial fetch if not in rehire mode
                if (!isRehireMode) {
                    // Check if rehire was previously in progress
                    const hasRehireData =
                        userData.rehireDetails?.rehireDate ||
                        Object.values(userData.rehireChecklist || {}).some(v => v === true);
                    
                    if (hasRehireData) {
                        setIsRehireMode(true);
                        if (userData.rehireChecklist) {
                            setRehireChecklist(userData.rehireChecklist);
                        }
                        if (userData.rehireDetails) {
                            setRehireDetails(userData.rehireDetails);
                        }
                    }
                }
            } else {
                console.error("API success, but user data is missing.");
                toast.error('Failed to load employee details: Data missing.');
            }
        } catch (error) {
            console.error("Error fetching employee details:", error);
            toast.error('Failed to load employee details');
            if (error.response && error.response.data && error.response.data.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error(`Server error: ${error.response ? error.response.status : 'Unknown'}`);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmployeeData();
    }, [userId]);

    const handleUpdateSuccess = () => {
        fetchEmployeeData();
    }

  

    if (loading) {
        return <Box sx={{ p: 3, textAlign: 'center' }}>Loading employee data...</Box>;
    }

    if (!employeeData) {
        return <Box sx={{ p: 3, textAlign: 'center' }}>Employee details not found or failed to load.</Box>;
    }

    const tabsData = [
        { label: 'Personal & Contact', icon: <PersonIcon /> },
        { label: 'Employment Details', icon: <WorkIcon /> },
        { label: 'Compensation & Pay', icon: <AttachMoneyIcon /> },
        { label: 'Banking & Statutory', icon: <VerifiedUserIcon /> },
        { label: 'Documents & Other', icon: <NoteAddIcon /> },
        { label: 'Assign Policies', icon: <PolicyIcon /> },
        { label: 'Exit / Rehire', icon: <ExitToAppIcon /> },
    ];

    const renderTabContent = () => {
        switch (activeTab) {
            case 0:
                return <PersonalContactDetails
                    userId={userId}
                    organizationId={organizationId}
                    initialData={employeeData}
                    navigationContext={navigationContext}
                    onSaveAndReturn={() => handleSaveAndReturnToRehire('personal')}
                    onUpdateEmployeeSuccess={handleUpdateSuccess}
                    // COMMENTED: Form completion callback
                    // onFormCompletionChange={(percent) => handleTabFormCompletionChange('personal', percent)}
                />;
            case 1:
                return <EmploymentDetails
                    userId={userId}
                    organizationId={organizationId}
                    initialData={employeeData}
                    navigationContext={navigationContext}
                    onSaveAndReturn={() => handleSaveAndReturnToRehire('employee')}
                    onUpdateEmployeeSuccess={handleUpdateSuccess}
                    // COMMENTED: Form completion callback
                    // onFormCompletionChange={(percent) => handleTabFormCompletionChange('employee', percent)}
                />;
            case 2:
                return <PayrollDetails
                    userId={userId}
                    organizationId={organizationId}
                    initialData={employeeData}
                    navigationContext={navigationContext}
                    onSaveAndReturn={() => handleSaveAndReturnToRehire('compensation')}
                    onUpdateEmployeeSuccess={handleUpdateSuccess}
                    // COMMENTED: Form completion callback
                    // onFormCompletionChange={(percent) => handleTabFormCompletionChange('compensation', percent)}
                />;
            case 3:
                return <BankingStatutory
                    userId={userId}
                    organizationId={organizationId}
                    initialData={employeeData}
                    navigationContext={navigationContext}
                    onSaveAndReturn={() => handleSaveAndReturnToRehire('banking')}
                    onUpdateEmployeeSuccess={handleUpdateSuccess}
                    // COMMENTED: Form completion callback
                    // onFormCompletionChange={(percent) => handleTabFormCompletionChange('banking', percent)}
                />;
            case 4:
                return <DocumentsOther
                    userId={userId}
                    organizationId={organizationId}
                    initialData={employeeData}
                    navigationContext={navigationContext}
                    onSaveAndReturn={() => handleSaveAndReturnToRehire('documents')}
                    onUpdateEmployeeSuccess={handleUpdateSuccess}
                    // COMMENTED: Form completion callback
                    // onFormCompletionChange={(percent) => handleTabFormCompletionChange('documents', percent)}
                />;
            case 5:
                return <PolicyAssignment
                    userId={userId}
                    organizationId={organizationId}
                    initialData={employeeData}
                    navigationContext={navigationContext}
                    onSaveAndReturn={() => handleSaveAndReturnToRehire('policies')}
                    onUpdateEmployeeSuccess={handleUpdateSuccess}
                    // COMMENTED: Form completion callback
                    // onFormCompletionChange={(percent) => handleTabFormCompletionChange('policies', percent)}
                />;
            case 6:
                return <ExitRehireStatus
                    userId={userId}
                    organizationId={organizationId}
                    initialData={employeeData}
                    isRehireMode={isRehireMode}
                    onStartRehire={handleStartRehire}
                    onRehireComplete={handleRehireComplete}
                    rehireChecklist={rehireChecklist}
                    rehireDetails={rehireDetails}
                    navigationContext={navigationContext}
                    onNavigateToTab={handleNavigateToTab}
                    onUpdateEmployeeSuccess={handleUpdateSuccess}
                    onUpdateRehireChecklist={handleUpdateRehireChecklist}
                    onUpdateRehireDetails={handleUpdateRehireDetails}                   
                />;
            default:
                return null;
        }
    };

    return (
        <Box sx={{
            paddingTop: "24px", paddingRight: "24px", paddingBottom: "24px", paddingLeft: "8px"
        }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
            }}>
                {/* Back Button */}
                <Button
                    startIcon={<ArrowBack />}
                    variant="outlined"
                    color="primary"
                    onClick={onClose}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", px: 2, py: 1 }}
                >
                    Back to Employees
                </Button>

                {/* View All Details Button */}
                <Button
                    variant="contained"   
                    color="primary"
                    onClick={handleModalOpen}
                    sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", px: 3, py: 1, mr: 4 }}
                >
                    View All Details
                </Button>
            </Box>

            <StyledTabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="employee details tabs with custom style"
                variant="scrollable"
                scrollButtons="auto"
            >
               {tabsData.map((tab, index) => {
                    // COMMENTED: Tab completion logic
                    /*
                    const tabKeys = ['personal', 'employee', 'compensation', 'banking', 'documents', 'policies', 'exit'];
                    const tabKey = tabKeys[index];
                    const completion = completionState[tabKey] || 0;
                    const isComplete = completion === 100;
                    */
                    return (
                        <Tab
                            key={index}
                            label={tab.label}
                            icon={
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5}}>
                                    {/* COMMENTED: Status dot logic */}
                                    {/* 
                                    {tabKey !== 'exit' && (
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                backgroundColor: isComplete ? 'green' : 'red',
                                            }}
                                        />
                                    )}
                                    */}
                                    {/* Icon */}
                                    {tab.icon}
                                </Box>
                            }
                            iconPosition="top"
                        />
                    );
                })}
            </StyledTabs>

            <ContentPaper>
                {renderTabContent()}
            </ContentPaper>

            {/* View All Details Modal Integration */}
            <ViewAllDetailsModal
                open={isModalOpen}
                handleClose={handleModalClose}
                employeeData={employeeData || mockAllEmployeeData}
            />
        </Box>
    );
};

export default ViewEmployeeDetails;